/* eslint-disable max-lines */

import _ from 'underscore'
import React, { useCallback, useState } from 'react'
import styled, { css } from 'styled-components'
import { NavLink, useNavigate } from 'react-router-dom'
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'

import ActionTakenInput from 'pages/shared/CustomerList/ActionTakenInput'
import ArchiveDeviceModal from 'pages/shared/ArchivedDevices/ArchiveDeviceModal'
import AlertIcons from 'pages/shared/CustomerList/CustomerDeviceList/AlertIcons'
import CancelSubscriptionModal from 'pages/HehmDevice/CancelSubscriptionModal'
import ConnectedStatusIcon from 'pages/shared/ConnectedStatusIcon'
import HealthStatusIcon from 'pages/shared/HealthStatusIcon'
import KeyboardAccessible from 'components/KeyboardAccessible'
import ServiceContractIcon from 'pages/shared/DeviceTabs/ServiceContractIcon'
import Table from 'components/Table'
import Tooltip from 'components/Tooltip'

import { useConfirmDialog } from 'hooks/useDialog'
import { useFeaturesContext } from 'hooks/FeaturesContext'
import useHehmDevice from 'hooks/useHehmDevice'
import useHehmDeviceHardware from 'hooks/useHehmDeviceHardware'
import useScreenSize from 'hooks/useScreenSize'
import useServerlessActiveAlerts from 'hooks/useServerlessActiveAlerts'

import { sortBasedOnSeverity, updateAlertsSeverity } from 'helpers/alerts'
import Brand from 'helpers/brand'
import { CONFIRMATION_DIALOG_MESSAGES } from 'helpers/confirmationDialogMessages'
import { hasAlerts } from 'helpers/device'
import { friendlyName, longDateTime } from 'helpers/formatters'
import { parseAlerts } from 'pages/Device/ThermostatRthPanel/Chart/rthDataParser'

import Api from 'services/api'

import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

import { ArchivedDevice } from 'types/ArchivedDevice'

import { Customer } from 'types/Customer'
import { Device } from 'types/Device'

type ArchiveDeviceState = {
  [index: string]: boolean
}

type Props = {
  customer: Customer
  device: Device
  updateRefreshTimestampCallback: () => void;
}

export const filterHealthFriendNames = {
  replacement: 'Needs Replacement'
}

const HehmIdentifiers = ({ deviceId }: { deviceId: string }) => {
  const { hehmDeviceHardware } = useHehmDeviceHardware(deviceId)
  const { hehmDevice } = useHehmDevice(deviceId)
  const filterCategory = hehmDevice?.health?.filter?.category?.toLowerCase()

  return (
    <>
      <div><span>IMEI: </span><S.Identifier>{hehmDeviceHardware?.modem?.imei}</S.Identifier></div>
      <div><span>Filter Health: </span><S.Identifier>{friendlyName(filterCategory, filterHealthFriendNames)}</S.Identifier></div>
    </>
  )
}

const DeviceRow = ({ customer, device, updateRefreshTimestampCallback }: Props) => {
  const navigate = useNavigate()
  const screenSize = useScreenSize()
  const [showArchiveDeviceModal, setShowArchiveDeviceModal] = useState<ArchiveDeviceState>({})
  const [showCancelSubscriptionModal, setShowCancelSubscriptionModal] = useState<ArchiveDeviceState>({})
  const [hoveredDeviceId, setHoveredDeviceId] = useState('')
  const { enabledFeatures } = useFeaturesContext()
  const { confirm } = useConfirmDialog()
  const componentType = 'System'

  const deviceHasAlerts = hasAlerts(device, true)

  const { activeAlerts, areActiveAlertsLoaded } = useServerlessActiveAlerts(device.deviceId, deviceHasAlerts)
  const historyDetails = updateAlertsSeverity(activeAlerts) ?? []
  const alerts = parseAlerts(historyDetails, device.timeZone || 'America/Chicago')
  const alertIds = _.map(sortBasedOnSeverity(alerts), r => r.id).join('-')

  const onCreateCallback = useCallback((device: Omit<ArchivedDevice, 'id' | 'createdAt'>) => {
    Api.ArchivedDevices.create(device.dealerId, device)
      .then(() => {
        updateRefreshTimestampCallback()
      })
  }, [updateRefreshTimestampCallback])

  const onDeleteSystem = useCallback(
    async (deviceId: string) => {
      const isConfirmed = await confirm({
        title: CONFIRMATION_DIALOG_MESSAGES.DELETE.TITLE(componentType),
        description: CONFIRMATION_DIALOG_MESSAGES.DELETE.DESCRIPTION(componentType),
        confirmText: CONFIRMATION_DIALOG_MESSAGES.BUTTON_TEXT.DELETE
      })

      if (isConfirmed) {
        Api.Device.delete(deviceId).then(() => {
          updateRefreshTimestampCallback()
        })
      }
    },
    [updateRefreshTimestampCallback, confirm]
  )

  const onDeleteHehmDevice = useCallback(
    async (dealerId: string, deviceId: string, reason: string) => {
      await Api.HomeHealth.deprovisionDevice(dealerId, deviceId, reason)
        .then(() => {
          updateRefreshTimestampCallback()
        })
    },
    [updateRefreshTimestampCallback]
  )

  const handleDeleteClick = useCallback(
    (device: Device) => {
      if (isHEHMDevice(device)) {
        setShowCancelSubscriptionModal(showCancelSubscriptionModal => ({ ...showCancelSubscriptionModal, [device.deviceId]: true }))
      } else {
        onDeleteSystem(device.deviceId)
      }
    },
    [onDeleteSystem]
  )

  const getDeviceLink = (deviceId: string) => {
    return `${deviceId}/dashboard`
  }

  const isHEHMDevice = (device: Device) => {
    return device.deviceType.toLowerCase() === 'hehm'
  }

  const canShowDeviceDisconnectedIcon = (
    device: Device
  ): JSX.Element | null => {
    if (device.disconnected24Hrs) {
      return (
        <Tooltip title="This HVAC Performance Monitoring kit has been disconnected more than 24 hours">
          <S.DisconnectedIcon src="/img/hehm_disconnected_icon.png" alt="Disconnected 24 hours" />
        </Tooltip>
      )
    }

    if (device.disconnected7Days) {
      return (
        <Tooltip title="This HVAC Performance Monitoring kit has been disconnected more than 7 days">
          <S.DisconnectedIcon src="/img/hehm_disconnected_seven_days_icon.png" alt="Disconnected 7 days" />
        </Tooltip>
      )
    }

    if (device.recentlyDisconnected) {
      return (
        <Tooltip title="This HVAC Performance Monitoring kit has been disconnected for less than 24 hours">
          <S.RecentlyDisconnectedIcon src="/img/hehm-disconnected.svg" alt="Disconnected < 24 hours" />
        </Tooltip>
      )
    }

    return null
  }

  const onClickHealthIcon = (deviceId: string) => {
    navigate(`/customers/${customer.id}/systems/${deviceId}/alerts`)
  }

  if (!device.matched || (isHEHMDevice(device) && !enabledFeatures.includes('hehm'))) {
    return null
  }

  return (
    <S.DeviceRow key={device.deviceId} onMouseEnter={() => { setHoveredDeviceId(device.deviceId) }} onMouseLeave={() => { setHoveredDeviceId('') }}>
      <S.DeviceCell>
        <S.DeviceTitle>
          <S.TitleArea>
            <NavLink to={`/customers/${customer.id}/systems/${getDeviceLink(device.deviceId)}`}>
              {device.registered && device.optedIn ? (device.name || device.deviceId)
                : (
                  <Tooltip title={device.status?.toLowerCase() === 'not enrolled' ? `Device not enrolled to customer's ${Brand.brandName().toUpperCase()} Home account.` : ''}>
                    <div>{device.status}</div>
                  </Tooltip>
                )
              }
            </NavLink>
            {device.registered && device.optedIn && !isHEHMDevice(device) && !device.connected && <S.ConnectedStatusIcon isConnected={device.connected} />}
            {!isHEHMDevice(device) && (
              <ServiceContractIcon
                displayIcon={hoveredDeviceId === device.deviceId}
                className={!device.serviceContractStatus ? 'serviceContractNone' : `serviceContract${device.serviceContractStatus}`}
                device={device}
              />
            )}
            {isHEHMDevice(device) && (
              <S.HealthStatusIcon
                status={device.healthScore}
                onClick={() => onClickHealthIcon(device.deviceId)}
              />
            )}
            {isHEHMDevice(device) && canShowDeviceDisconnectedIcon(device)}
          </S.TitleArea>
        </S.DeviceTitle>
        <S.Identifiers>
          <div><span>AUID: </span><S.Identifier>{device.deviceId}</S.Identifier></div>
          <div><span>S/N: </span><S.Identifier>{device.serialNumber}</S.Identifier></div>
          {isHEHMDevice(device) && <HehmIdentifiers deviceId={device.deviceId} />}
        </S.Identifiers>
      </S.DeviceCell>

      <S.Cell>
        {device.registered && device.optedIn && (
          <>
            { screenSize === 'small' && <S.SmallScreenLabel>Model:</S.SmallScreenLabel> }
            <span>{device.displayModel}</span>
          </>
        )}
      </S.Cell>
      <S.AlertsCell>
        {device.registered && device.optedIn && device.connected && (
          <AlertIcons
            device={device}
            customerId={customer.id}
            alertDetails={alerts}
            alertIds={alertIds}
            areAlertsLoaded={areActiveAlertsLoaded}
          />
        )}
      </S.AlertsCell>
      <S.Cell>
        {device?.createdAt && (
          <>
            { screenSize === 'small' && <S.SmallScreenLabel>Date Added:</S.SmallScreenLabel> }
            <span>{longDateTime(device?.createdAt)}</span>
          </>
        )}
      </S.Cell>
      <S.ActionTakenCell>
        { screenSize === 'small' && <S.SmallScreenActionTakenLabel>Action Taken</S.SmallScreenActionTakenLabel> }
        <div>
          <ActionTakenInput device={device} />
        </div>

        {device.registered && device.optedIn && !isHEHMDevice(device)
          ? (
            <Tooltip title="Archive Device">
              <S.RemoveAction
                onClick={() => setShowArchiveDeviceModal(showArchiveDeviceModal => ({ ...showArchiveDeviceModal, [device.deviceId]: true }))}
              >
                <S.ArchiveOutlinedIcon role="img" />
              </S.RemoveAction>
            </Tooltip>
          )
          : (
            <Tooltip title="Delete Device">
              <S.RemoveAction
                onClick={() => { handleDeleteClick(device) }}
              >
                <S.DeleteOutlinedIcon role="img" />
              </S.RemoveAction>
            </Tooltip>
          )
        }

        <ArchiveDeviceModal
          isVisible={showArchiveDeviceModal[device.deviceId]}
          closeCallback={() => setShowArchiveDeviceModal(showArchiveDeviceModal => ({ ...showArchiveDeviceModal, [device.deviceId]: false }))}
          actionType="create"
          auid={device.deviceId}
          onUpdateCallback={async (reason) => onCreateCallback({ auid: device.deviceId, dealerId: device.dealerId, registered: device.registered, reason })}
        />

        <CancelSubscriptionModal
          isVisible={showCancelSubscriptionModal[device.deviceId]}
          closeCallback={() => setShowCancelSubscriptionModal(showCancelSubscriptionModal => ({ ...showCancelSubscriptionModal, [device.deviceId]: false }))}
          deviceId={device.deviceId}
          onCancelCallback={async (reason) => onDeleteHehmDevice(device.dealerId.toString(), device.deviceId, reason)}
        />
      </S.ActionTakenCell>
    </S.DeviceRow>
  )
}

export default DeviceRow

const iconStyle = (iconType: string) => {
  return css`
    margin: 8px 0 0 10px;
    cursor: pointer;
    color: ${color(iconType)};

    &:hover {
      color: ${color(`${iconType}Hover`)};
    }
  `
}

const Cell = styled(Table.Cell)`
  overflow: hidden;
  flex: 1;
  padding: 10px 0;

  > :first-child {
    margin-left: 15px;
  }

  @media ${screenSize.small} {
    padding: 5px 0 0 30px;

    > :first-child {
      margin-left: 0;
    }
  }
`

const SmallScreenLabel = styled.span`
  display: none;
  margin-right: 5px;

  @media ${screenSize.small} {
    display: block;
  }
`

const S = {
  Cell,
  DeviceRow: styled(Table.Row)`
    padding: 20px 0 20px 0;

    &:last-child {
      border-bottom: none;
    }

    & .serviceContract:focus .serviceContractNone {
      display: block;
    }
    &:hover .serviceContractNone,
    & .serviceContractExpired,
    & .serviceContractActive {
      display: block;
    }
  `,
  DeviceCell: styled(Table.Cell)`
    flex-direction: column;
  `,
  DeviceTitle: styled.div`
    display: grid;
    grid-template-columns: 1fr 26px;
  `,
  TitleArea: styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    position: relative;
  `,
  Identifiers: styled.div`
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;
  `,
  Identifier: styled.span`
    overflow-wrap: anywhere;
    text-transform: capitalize;
  `,
  ConnectedStatusIcon: styled(ConnectedStatusIcon)`
    position: absolute;
    margin-top: 1px;
    margin-left: 10px;
  `,
  HealthStatusIcon: styled(HealthStatusIcon)`
    position: relative;
    top: -1px;  
    cursor: pointer;
  `,
  AlertsCell: styled(Table.Cell)`
    overflow: hidden;
    flex: 1;
    padding: 10px 0;

    @media ${screenSize.medium} {
      flex-wrap: wrap;
    }

    @media ${screenSize.small} {
      padding: 5px 0 0 25px;
      margin-bottom: -5px;
    }
  `,

  RemoveAction: styled(KeyboardAccessible)`
    height: 40px;
  `,
  ActionTakenCell: styled(Cell)`
    flex-direction: row;
    margin-bottom: -15px;
    padding: 0;

    @media ${screenSize.small} {
      margin-left: 30px;
      margin-top: 5px;
    }
  `,
  DeleteOutlinedIcon: styled(DeleteOutlinedIcon)`
    ${iconStyle('destructiveIcon')}
    margin: 10px;
  `,
  ArchiveOutlinedIcon: styled(ArchiveOutlinedIcon)`
    ${iconStyle('actionableIcon')}
    margin: 10px;
  `,
  SmallScreenLabel,
  SmallScreenActionTakenLabel: styled(SmallScreenLabel)`
    text-wrap: nowrap;
    margin-top: 9px;
    margin-right: 10px;
  `,
  DisconnectedIcon: styled.img`
    width: 1em;
    height: 1em;
    margin-left: 10px;
    cursor: pointer;
  `,
  RecentlyDisconnectedIcon: styled.img`
    width: 1em;
    height: 1em;
    margin-left: 10px;
    cursor: pointer;
    filter: brightness(0);
  `,
  ServiceContractIcon: styled(ServiceContractIcon)`
    margin: 0 4px;
    height: 24px;
  `
}
